/**
 * Customise menu (Hub staff nav): drag to reorder, put pages into groups,
 * rename or ungroup groups, hide pages, or go back to the standard menu.
 * Saved per user (lib/navLayout.ts). Only arranges pages the user can
 * already open; permissions still decide what's there.
 */
import { useMemo, useState } from "react";
import { Reorder, motion, useDragControls } from "framer-motion";
import { cn } from "@/lib/utils";
import { flatten, newGroupId, type LayoutEntry, type NavEntry, type NavItem, type NavLayout } from "@/lib/navLayout";

const btn = "h-9 px-3.5 rounded-xl text-body-sm font-medium focus-ring disabled:opacity-40 disabled:pointer-events-none";
const btnOutline = cn(btn, "bg-white dark:bg-dark-700 border border-surface-border dark:border-dark-500 text-dark-600 dark:text-dark-100 hover:bg-surface-secondary dark:hover:bg-dark-600");
const btnPrimary = cn(btn, "bg-brand-500 text-white hover:bg-brand-600");
const iconBtn = "w-7 h-7 flex items-center justify-center rounded-lg text-dark-300 hover:text-dark-600 dark:hover:text-dark-100 hover:bg-surface-secondary dark:hover:bg-dark-600";

type Group = Extract<LayoutEntry, { t: "g" }>;
const keyOf = (e: LayoutEntry) => (e.t === "i" ? `i:${e.to}` : `g:${e.id}`);

function Grip() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" /></svg>;
}
function EyeOff() {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>;
}

/** Drag handle row wrapper. */
function Row({ value, children, className }: { value: string; children: (start: (e: React.PointerEvent) => void) => React.ReactNode; className?: string }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={value} dragListener={false} dragControls={controls} className={cn("list-none", className)}>
      {children((e) => controls.start(e))}
    </Reorder.Item>
  );
}

function Handle({ start }: { start: (e: React.PointerEvent) => void }) {
  return (
    <span onPointerDown={start} style={{ touchAction: "none" }} title="Drag to move"
      className="w-6 h-7 flex items-center justify-center text-dark-300 cursor-grab active:cursor-grabbing shrink-0">
      <Grip />
    </span>
  );
}

export default function NavCustomiseModal({ defaults, current, folderIcon, onSave, onReset, onClose }: {
  /** The standard, permission-filtered menu. */
  defaults: NavEntry[];
  /** What the editor starts from: their saved layout, or the standard one. */
  current: NavLayout;
  folderIcon: any;
  onSave: (layout: NavLayout) => Promise<void>;
  onReset: () => Promise<void>;
  onClose: () => void;
}) {
  const pages = useMemo(() => new Map(flatten(defaults).map((f) => [f.item.to, f.item])), [defaults]);
  const groupIcons = useMemo(() => new Map(defaults.filter((e): e is Extract<NavEntry, { children: NavItem[] }> => "children" in e)
    .map((g) => [`d:${g.label}`, g.icon])), [defaults]);
  const [entries, setEntries] = useState<LayoutEntry[]>(() => current.entries);
  const [hidden, setHidden] = useState<string[]>(() => current.hidden.filter((h) => pages.has(h)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [focusId, setFocusId] = useState<string | null>(null);

  const groups = entries.filter((e): e is Group => e.t === "g");

  /** Takes a page out of wherever it is; returns the entries and where it was. */
  const without = (list: LayoutEntry[], to: string): { list: LayoutEntry[]; top: number; group: string | null; at: number } => {
    let top = -1; let group: string | null = null; let at = -1;
    const next = list.flatMap((e, i): LayoutEntry[] => {
      if (e.t === "i") {
        if (e.to === to) { top = i; return []; }
        return [e];
      }
      const k = e.children.indexOf(to);
      if (k >= 0) { group = e.id; at = k; top = i; return [{ ...e, children: e.children.filter((c) => c !== to) }]; }
      return [e];
    });
    return { list: next, top, group, at };
  };

  const moveTo = (to: string, target: string) => {
    const id = target === "new" ? newGroupId() : "";
    if (id) setFocusId(id);
    setEntries((list) => {
      const { list: rest, top, group } = without(list, to);
      if (target === "top") {
        // Out of a group: just below that group.
        const at = group ? rest.findIndex((e) => e.t === "g" && e.id === group) + 1 : top;
        return [...rest.slice(0, at), { t: "i", to }, ...rest.slice(at)];
      }
      if (target === "new") {
        const g: LayoutEntry = { t: "g", id, label: "New group", defaultId: null, children: [to] };
        const at = group ? rest.findIndex((e) => e.t === "g" && e.id === group) + 1 : top;
        return [...rest.slice(0, at), g, ...rest.slice(at)];
      }
      return rest.map((e) => (e.t === "g" && e.id === target ? { ...e, children: [...e.children, to] } : e));
    });
  };

  const hide = (to: string) => {
    setEntries((list) => without(list, to).list);
    setHidden((h) => [...h, to]);
  };
  const show = (to: string) => {
    setHidden((h) => h.filter((x) => x !== to));
    setEntries((list) => [...list, { t: "i", to }]);
  };
  const rename = (id: string, label: string) =>
    setEntries((list) => list.map((e) => (e.t === "g" && e.id === id ? { ...e, label } : e)));
  const ungroup = (id: string) =>
    setEntries((list) => list.flatMap((e): LayoutEntry[] =>
      e.t === "g" && e.id === id ? e.children.map((to) => ({ t: "i", to })) : [e]));
  const addGroup = () => {
    const id = newGroupId();
    setFocusId(id);
    setEntries((list) => [...list, { t: "g", id, label: "New group", defaultId: null, children: [] }]);
  };
  const reorderTop = (keys: string[]) => {
    const byKey = new Map(entries.map((e) => [keyOf(e), e]));
    setEntries(keys.map((k) => byKey.get(k)!).filter(Boolean));
  };
  const reorderKids = (id: string, kids: string[]) =>
    setEntries((list) => list.map((e) => (e.t === "g" && e.id === id ? { ...e, children: kids } : e)));

  const unnamed = groups.some((g) => !g.label.trim());
  const save = async () => {
    setBusy(true); setError("");
    try {
      await onSave({
        entries: entries
          .filter((e) => e.t === "i" || e.children.length > 0)
          .map((e) => (e.t === "g" ? { ...e, label: e.label.trim() } : e)),
        hidden,
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "It wasn't saved");
      setBusy(false);
    }
  };
  const reset = async () => {
    setBusy(true); setError("");
    try { await onReset(); } catch (e: any) { setError(e?.message || "It wasn't reset"); setBusy(false); }
  };

  const moveSelect = (to: string, inGroup: string | null) => (
    <select value="" disabled={busy} aria-label="Move to"
      onChange={(e) => e.target.value && moveTo(to, e.target.value)}
      className="h-7 rounded-lg border border-surface-border dark:border-dark-500 bg-white dark:bg-dark-700 text-caption text-dark-500 dark:text-dark-200 px-1.5 max-w-[118px]">
      <option value="">Move to…</option>
      {inGroup && <option value="top">Top level</option>}
      {groups.filter((g) => g.id !== inGroup).map((g) => <option key={g.id} value={g.id}>{g.label.trim() || "Unnamed group"}</option>)}
      <option value="new">New group</option>
    </select>
  );

  const pageRow = (to: string, inGroup: string | null, start: (e: React.PointerEvent) => void) => {
    const p = pages.get(to);
    if (!p) return null;
    return (
      <div className="flex items-center gap-1.5 rounded-xl border border-surface-border dark:border-dark-500 bg-white dark:bg-dark-700 pl-1 pr-1.5 py-1">
        <Handle start={start} />
        <p.icon size={16} className="text-dark-400 shrink-0" />
        <span className="flex-1 text-body-sm text-dark-700 dark:text-dark-100 truncate">{p.label}</span>
        {moveSelect(to, inGroup)}
        <button type="button" className={iconBtn} title="Hide from menu" aria-label={`Hide ${p.label}`} onClick={() => hide(to)} disabled={busy}>
          <EyeOff />
        </button>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-lg bg-white dark:bg-dark-800 rounded-2xl shadow-2xl my-10 flex flex-col max-h-[calc(100vh-5rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-3">
          <h2 className="text-heading-sm text-dark-800 dark:text-white">Customise menu</h2>
          <p className="text-body-sm text-dark-400 mt-0.5">Drag to reorder. Only you see these changes.</p>
        </div>

        <motion.div layoutScroll className="flex-1 overflow-y-auto px-6 pb-2">
          <Reorder.Group axis="y" values={entries.map(keyOf)} onReorder={reorderTop} className="space-y-1.5">
            {entries.map((e) => (
              <Row key={keyOf(e)} value={keyOf(e)}>
                {(start) => e.t === "i" ? pageRow(e.to, null, start) : (
                  <div className="rounded-xl border border-surface-border dark:border-dark-500 bg-surface-secondary/60 dark:bg-dark-600/40 p-1.5">
                    <div className="flex items-center gap-1.5 pr-0.5">
                      <Handle start={start} />
                      {(() => { const I = (e.defaultId && groupIcons.get(e.defaultId)) || folderIcon; return <I size={16} className="text-dark-400 shrink-0" />; })()}
                      <input value={e.label} maxLength={40} disabled={busy} autoFocus={focusId === e.id}
                        onFocus={(ev) => focusId === e.id && ev.target.select()}
                        onChange={(ev) => rename(e.id, ev.target.value)} aria-label="Group name"
                        className={cn("flex-1 min-w-0 h-7 px-2 rounded-lg bg-transparent text-body-sm font-medium text-dark-700 dark:text-dark-100 border focus-ring",
                          e.label.trim() ? "border-transparent hover:border-surface-border dark:hover:border-dark-500" : "border-status-error")} />
                      <button type="button" onClick={() => ungroup(e.id)} disabled={busy}
                        className="h-7 px-2 rounded-lg text-caption text-dark-500 dark:text-dark-200 hover:bg-white dark:hover:bg-dark-600">
                        Ungroup
                      </button>
                    </div>
                    <div className="ml-6 mt-1.5">
                      {e.children.length === 0 ? (
                        <p className="text-caption text-dark-400 px-2 py-1.5">Empty. Use Move to… on a page to put it here.</p>
                      ) : (
                        <Reorder.Group axis="y" values={e.children} onReorder={(kids) => reorderKids(e.id, kids)} className="space-y-1">
                          {e.children.map((to) => (
                            <Row key={to} value={to}>{(s) => pageRow(to, e.id, s)}</Row>
                          ))}
                        </Reorder.Group>
                      )}
                    </div>
                  </div>
                )}
              </Row>
            ))}
          </Reorder.Group>

          <button type="button" onClick={addGroup} disabled={busy}
            className="mt-2 w-full h-9 rounded-xl border border-dashed border-surface-border dark:border-dark-500 text-body-sm text-dark-500 dark:text-dark-200 hover:bg-surface-secondary dark:hover:bg-dark-600">
            + New group
          </button>

          {hidden.length > 0 && (
            <div className="mt-4">
              <p className="text-caption font-medium text-dark-500 dark:text-dark-200 mb-1.5">Hidden</p>
              <div className="space-y-1">
                {hidden.map((to) => {
                  const p = pages.get(to);
                  if (!p) return null;
                  return (
                    <div key={to} className="flex items-center gap-2 rounded-xl border border-dashed border-surface-border dark:border-dark-500 px-2.5 py-1.5">
                      <p.icon size={16} className="text-dark-300 shrink-0" />
                      <span className="flex-1 text-body-sm text-dark-400 truncate">{p.label}</span>
                      <button type="button" onClick={() => show(to)} disabled={busy}
                        className="h-7 px-2 rounded-lg text-caption text-brand-600 dark:text-accent-300 hover:bg-surface-secondary dark:hover:bg-dark-600">
                        Show
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>

        <div className="px-6 py-4 border-t border-surface-border dark:border-dark-500">
          {error && <p className="text-body-sm text-status-error mb-2">{error}</p>}
          {unnamed && <p className="text-caption text-status-error mb-2">Give every group a name.</p>}
          <div className="flex items-center justify-between gap-2">
            <button type="button" className={btnOutline} disabled={busy} onClick={reset}>Reset to standard</button>
            <div className="flex gap-2">
              <button type="button" className={btnOutline} disabled={busy} onClick={onClose}>Cancel</button>
              <button type="button" className={btnPrimary} disabled={busy || unnamed} onClick={save}>{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
